import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Container,
  Paper,
  Stack,
  Step,
  StepLabel,
  Stepper,
  Typography,
} from "@mui/material";
import LockRoundedIcon from "@mui/icons-material/LockRounded";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";
import logo from "../../assets/png/CodeNekt_for_Fleet_Logo.png";
import { useGetBillingConfig, useGetPlans, ValidatedPromoCode } from "../../api/billing";
import { useOnboardingCheckout, useRegister } from "../../api/registration";
import PlanSelector, { PlanSelection } from "../Billing/components/PlanSelector";
import PromoCodeInput from "../Billing/components/PromoCodeInput";
import { buildQuote, formatMoney, RECOMMENDED_PLAN_SLUG } from "../Billing/pricing";
import { getCurrentFleetId } from "../../utils/getCurrentFleet";
import AccountStep, { AccountFormValues, emptyAccountForm } from "./AccountStep";
import { AuthThemeScope } from "../../theme/AuthThemeScope";

type StepId = 0 | 1;

/** Display names for the payment providers the API can report. */
const PROVIDER_LABELS: Record<string, string> = {
  stripe: "Stripe",
  revolut: "Revolut",
};

/**
 * Self-serve signup.
 *
 * Two steps, one commit point:
 *
 *   0. Account + company  â†’ POST /auth/register  (creates the tenant, logs in)
 *   1. Plan + payment     â†’ POST â€¦/checkout-session â†’ redirect to Stripe
 *
 * The account is created BEFORE payment because Stripe Checkout is an
 * authenticated, company-scoped call â€” there is no company to bill until one
 * exists. That leaves a window where a user has an account but no
 * subscription; `/register/plan` is the resume point for exactly that state,
 * and RequireSubscription routes them here on their next login.
 *
 * Card details never touch this app. Step 1 hands off to Stripe-hosted
 * Checkout, so this origin stays out of PCI scope.
 */
export const Register: React.FC = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Resume mode: the account already exists (user is authenticated) and only
  // payment is outstanding. Reached via /register/plan.
  const alreadyRegistered = !!localStorage.getItem("token");
  const existingCompanyId = alreadyRegistered ? getCurrentFleetId() : 0;

  const [step, setStep] = useState<StepId>(alreadyRegistered ? 1 : 0);
  const [account, setAccount] = useState<AccountFormValues>(emptyAccountForm);
  const [companyId, setCompanyId] = useState<number>(existingCompanyId);
  const [fatalError, setFatalError] = useState<string | null>(null);

  const wasCancelled = searchParams.get("cancelled") === "1";

  const { plans, isPending: plansLoading } = useGetPlans();
  const { config: billingConfig } = useGetBillingConfig();
  const register = useRegister();
  const checkout = useOnboardingCheckout();

  // Trial length comes from the API (admin-managed) rather than a literal.
  // `trial_days` is already 0 when trials are switched off, so the two flags
  // cannot disagree.
  const trialDays = billingConfig.trial_days;
  const trialEnabled = billingConfig.trial_enabled && trialDays > 0;

  // Recomputed only when the length changes, so the displayed charge date does
  // not drift by a day if the component re-renders either side of midnight.
  const trialEndDate = useMemo(
    () => new Date(Date.now() + trialDays * 864e5),
    [trialDays]
  );

  // Monthly is the opening cycle here as well as on the Billing page. Signup
  // and Billing quote from the same pricing module, so they must also start on
  // the same cycle — otherwise a prospect is quoted a year up front, subscribes,
  // and then sees a different headline number the first time they open Billing.
  const [selection, setSelection] = useState<PlanSelection>({
    planSlug: null,
    addonSlug: null,
    cycle: "monthly",
    vehicleQuantity: 10,
  });

  /*
   * Promo code, only when the active provider expects it from us.
   *
   * Stripe collects the code on its own hosted page, so rendering a second
   * field here would give the customer two places to enter one and no way to
   * reconcile a mismatch. Revolut's page has no promo field at all, which is
   * why the code has to be captured before the redirect.
   */
  const collectPromoHere =
    billingConfig.promo_enabled &&
    billingConfig.capabilities.promo_collected_by === "app";

  const [promo, setPromo] = useState<ValidatedPromoCode | null>(null);

  // Preselect the recommended plan once the catalog arrives, but never
  // overwrite a choice the user has already made.
  useEffect(() => {
    if (selection.planSlug || plans.length === 0) return;
    const recommended =
      plans.find((p) => p.tier === "saas" && p.slug === RECOMMENDED_PLAN_SLUG) ??
      plans.find((p) => p.tier === "saas");
    if (recommended) setSelection((s) => ({ ...s, planSlug: recommended.slug }));
  }, [plans, selection.planSlug]);

  const selectedPlan = plans.find((p) => p.slug === selection.planSlug) ?? null;
  const selectedAddon = plans.find((p) => p.slug === selection.addonSlug) ?? null;
  const quote = useMemo(
    () => buildQuote(selectedPlan, selectedAddon, selection.cycle, selection.vehicleQuantity),
    [selectedPlan, selectedAddon, selection.cycle, selection.vehicleQuantity]
  );

  /*
   * What the promo actually takes off the first charge.
   *
   * Recomputed client-side purely for display; the API recalculates it when
   * building the plan, so this can never be the number that gets billed. It is
   * shown because a customer who applies a code and sees the total unchanged
   * reasonably concludes it did not work.
   */
  const discountCents = useMemo(() => {
    if (!promo) return 0;
    if (promo.discount_type === "percent") {
      return Math.round((quote.totalCents * (promo.percent_off ?? 0)) / 100);
    }
    return Math.min(quote.totalCents, promo.amount_off_cents ?? 0);
  }, [promo, quote.totalCents]);

  // A code that clears the whole balance cannot be billed as a cycle, so the
  // API rejects it. Caught here too, before the customer commits to a redirect
  // that would only fail.
  const promoCoversEverything = !!promo && discountCents >= quote.totalCents;

  // Clearing the code when the plan changes is deliberate: codes can be
  // plan-scoped, so one validated against the old plan may not apply to the
  // new one, and silently keeping it would show a discount that checkout then
  // refuses.
  useEffect(() => {
    if (promo) setPromo(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selection.planSlug]);

  // â”€â”€ Step 0 â†’ create the tenant â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
  const handleCreateAccount = () => {
    setFatalError(null);
    register.mutate(
      {
        email: account.email.trim(),
        password: account.password,
        password_confirmation: account.password_confirmation,
        firstname: account.firstname.trim(),
        lastname: account.lastname.trim(),
        company_name: account.company_name.trim(),
        // A rate, not a VAT number: company.vat is decimal(10,2). Sent as a
        // number so the API's `numeric` rule sees what it expects, and the
        // comma separator a French user types is normalised first.
        vat: account.vat.trim()
          ? Number(account.vat.trim().replace(",", "."))
          : null,
        registration_id: account.registration_id.trim() || null,
        /*
          Transmis depuis le formulaire, plus en dur.

          Ces deux champs valaient `true` litteral : la requete affirmait un
          consentement que personne n'avait forcement donne, et la regle
          `required|accepted` de l'API ne pouvait par construction jamais
          echouer. Une preuve de consentement fabriquee par l'emetteur ne
          prouve rien — c'est precisement ce qu'un DPA doit etablir.

          Aucune version n'est envoyee : c'est l'API qui decide laquelle est en
          vigueur (config/legal.php) et qui l'ecrit dans `legal_consent`. Un
          client ne peut pas se voir accorder le droit de declarer quelle
          version il a acceptee.
        */
        accept_terms: account.accept_terms,
        accept_dpa: account.accept_dpa,
      },
      {
        onSuccess: (user) => {
          const cid = user?.company_users?.find(
            (cu) => cu.company_role === "FLEET_MANAGER" && Number(cu.company_id) > 0
          )?.company_id;

          if (!cid) {
            // The account exists but we cannot bill it. Surfacing this rather
            // than silently advancing avoids a checkout call that would 404.
            setFatalError(
              t("register.errorNoCompany", "Account created, but the company could not be resolved. Please sign in again.")
            );
            return;
          }
          setCompanyId(Number(cid));
          setStep(1);
        },
      }
    );
  };

  // â”€â”€ Step 1 â†’ Stripe Checkout â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
  const handleGoToPayment = () => {
    if (!selection.planSlug || !companyId) return;
    setFatalError(null);

    checkout.mutate(
      {
        companyId,
        plan_slug: selection.planSlug,
        billing_cycle: selection.cycle,
        vehicle_quantity: selection.vehicleQuantity,
        ai_addon_slug: selection.addonSlug,
        // Sent only when this provider expects it. On Stripe the code travels
        // with the customer to the hosted page instead.
        promo_code: collectPromoHere ? promo?.code ?? null : null,
      },
      {
        onSuccess: ({ url }) => {
          if (url) {
            // Full navigation, not react-router: Stripe Checkout is a
            // different origin.
            window.location.href = url;
          }
        },
      }
    );
  };

  const busy = register.isPending || checkout.isPending;

  return (
    /*
      Signup is always dark, like the login screen.

      This box used to be `bgcolor: "background.default"`, which follows the
      app theme — so toggling light mode anywhere turned signup white while
      login stayed hardcoded dark. Two halves of one funnel, looking like two
      products, decided by a preference most visitors have never set.

      AuthThemeScope supplies the background, so no colour is set here.
    */
    <AuthThemeScope>
      <Box sx={{ py: { xs: 3, md: 6 } }}>
      <Container maxWidth="md">
        <Stack alignItems="center" spacing={1} mb={4}>
          <img src={logo} alt="CodeNekt for Fleet" style={{ height: 56 }} />
          <Typography variant="h5" fontWeight={800} textAlign="center">
            {t("register.title", "Create your fleet account")}
          </Typography>
          <Typography color="text.secondary" fontSize={14} textAlign="center">
            {trialEnabled
              ? t("register.subtitle", {
                  defaultValue:
                    "{{days}} days free. Cancel any time before the trial ends.",
                  days: trialDays,
                })
              : t(
                  "register.subtitleNoTrial",
                  "Create your account and choose a plan to get started."
                )}
          </Typography>
        </Stack>

        <Stepper activeStep={step} sx={{ mb: 4 }}>
          <Step>
            <StepLabel>{t("register.stepAccount", "Your account")}</StepLabel>
          </Step>
          <Step>
            <StepLabel>{t("register.stepPlan", "Plan & payment")}</StepLabel>
          </Step>
        </Stepper>

        {wasCancelled && (
          <Alert severity="info" sx={{ mb: 3 }}>
            {t(
              "register.paymentCancelled",
              "Payment was cancelled. Your account is saved â€” pick a plan to finish."
            )}
          </Alert>
        )}

        {fatalError && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {fatalError}
          </Alert>
        )}

        <Paper variant="outlined" sx={{ p: { xs: 2.5, md: 4 } }}>
          {step === 0 && (
            <AccountStep
              values={account}
              onChange={setAccount}
              onSubmit={handleCreateAccount}
              submitting={register.isPending}
            />
          )}

          {step === 1 && (
            <Stack spacing={3}>
              {plansLoading ? (
                <Stack alignItems="center" py={6}>
                  <CircularProgress />
                </Stack>
              ) : (
                <>
                  <PlanSelector
                    plans={plans}
                    value={selection}
                    onChange={setSelection}
                    disabled={busy}
                  />

                  {collectPromoHere && companyId > 0 && (
                    <PromoCodeInput
                      companyId={companyId}
                      planId={selectedPlan?.id ?? null}
                      value={promo}
                      onChange={setPromo}
                      disabled={busy}
                    />
                  )}

                  {promoCoversEverything && (
                    <Alert severity="warning">
                      {t(
                        "billing.promoCoversAll",
                        "Ce code couvre la totalité du montant. Un paiement de 0 ne peut pas être traité — contactez le support pour bénéficier d'une période offerte."
                      )}
                    </Alert>
                  )}

                  {/*
                    The trial length is admin-configurable, so both the wording
                    and the charge date have to come from the API. This notice
                    is shown at the point the customer hands over a card, so a
                    stale "you are not charged until <date>" here is a promise
                    the billing system would not keep.
                  */}
                  <Alert severity="info" icon={<LockRoundedIcon fontSize="small" />}>
                    {trialEnabled
                      ? t("register.trialNotice", {
                          defaultValue:
                            "Your card is required to start, but you are not charged until the {{days}}-day trial ends on {{date}}.",
                          days: trialDays,
                          date: trialEndDate.toLocaleDateString(i18n.language),
                        })
                      : t(
                          "register.noTrialNotice",
                          "Your card is charged today when you confirm your plan."
                        )}
                  </Alert>

                  <Stack
                    direction={{ xs: "column", sm: "row" }}
                    spacing={2}
                    justifyContent="space-between"
                    alignItems={{ sm: "center" }}
                  >
                    <Typography fontSize={13.5} color="text.secondary">
                      {trialEnabled
                        ? t("register.afterTrial", "After the trial")
                        : t("register.priceLabel", "Price")}
                      :{" "}
                      {/*
                        With a code applied the list price is struck through
                        rather than replaced. Showing only the discounted figure
                        hides what the code is worth, and showing only the list
                        price makes a valid code look like it did nothing.
                      */}
                      {discountCents > 0 && (
                        <Typography
                          component="span"
                          sx={{ textDecoration: "line-through", opacity: 0.6, mr: 0.75 }}
                        >
                          {formatMoney(
                            quote.totalCents,
                            selectedPlan?.currency ?? "EUR",
                            i18n.language
                          )}
                        </Typography>
                      )}
                      <strong>
                        {formatMoney(
                          quote.totalCents - discountCents,
                          selectedPlan?.currency ?? "EUR",
                          i18n.language
                        )}
                      </strong>{" "}
                      {selection.cycle === "annual"
                        ? t("billing.perYear", "/ year")
                        : t("billing.perMonth", "/ month")}
                    </Typography>

                    <Button
                      variant="contained"
                      size="large"
                      onClick={handleGoToPayment}
                      disabled={busy || !selection.planSlug || !companyId || promoCoversEverything}
                      startIcon={
                        checkout.isPending ? (
                          <CircularProgress size={16} color="inherit" />
                        ) : (
                          <LockRoundedIcon />
                        )
                      }
                    >
                      {/*
                        The label has to follow the trial, not assume it.
                        With trials switched off this button read "Start free
                        trial" directly above a notice saying the card is
                        charged today — the screen contradicted itself at the
                        exact moment the customer decides to hand over a card.
                      */}
                      {trialEnabled
                        ? t("register.startTrial", "Start free trial")
                        : t("register.subscribeNow", "Confirm and pay")}
                    </Button>
                  </Stack>

                  <Typography fontSize={12} color="text.secondary" textAlign="center">
                    {/*
                      The provider is interpolated, not hardcoded.
                      This named Stripe in all six locales, so after the switch
                      customers were told Stripe handled a payment Revolut was
                      about to take — then landed on a Revolut-branded page.
                      A payment-security notice naming the wrong processor
                      undermines the exact trust it exists to build.
                    */}
                    {t("register.securePaymentNotice", {
                      defaultValue:
                        "Payment is handled by {{provider}}. We never see or store your card details.",
                      provider: PROVIDER_LABELS[billingConfig.provider] ?? billingConfig.provider,
                    })}
                  </Typography>
                </>
              )}
            </Stack>
          )}
        </Paper>

        {step === 1 && !alreadyRegistered && (
          <Stack alignItems="center" mt={2}>
            <Button size="small" onClick={() => navigate("/dashboard")} disabled={busy}>
              {t("register.finishLater", "Finish later")}
            </Button>
          </Stack>
        )}
      </Container>
      </Box>
    </AuthThemeScope>
  );
};

export default Register;
